import { Box, CardContent, Dialog, DialogContent, Grid, Pagination, Stack, Typography } from '@mui/material';
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { MprcCard, MprcCardActions, MprcConfirmDialog, MprcFileSelection, MprcIconButtonMoreOptionsDropdown, MprcJoinComponents, MprcLink, MprcText } from 'components';
import { MprcFilterBoxContent, MprcFilterCount } from 'components/filter';
import { MprcCardFilterBox } from 'components/filter/MprcCardFilterBox';
import { MprcBarTitle } from 'components/MprcBarTitle';
import { MprcContainer } from 'components/MprcContainer';
import { MprcDialogTitleClose } from 'components/MprcDialogTitleClose';
import { MprcDropdownBuildGroupedItems, MprcDropDownItemAny, MprcDropdownItems } from 'components/MprcIconButtonDropdown';
import { MprcRequestErrorDialog } from 'components/MprcRequestErrorDialog';
import { MprcRequestSnackbarErrorReport } from 'components/MprcRequestErrorReport';
import { MprcRequestIndicator } from 'components/MprcRequestIndicator';
import { MprcSearchTextField } from "components/MprcSearchTextField";
import { MprcTextViewer } from 'components/MprcTextViewer';
import { FileUploadResultDto, ManagedUserRecordFilesResponse, ManagedUserRecordFilesResponsePaginatedList } from 'generated/CoreApi_v1';
import { Formaters } from "lib/Formaters";
import HtmlUtils from 'lib/HtmlUtils';
import LibUtils from "lib/LibUtils";
import MediaUtils from "lib/MediaUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from 'providers/CoreApiProvider';
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardLightBox } from 'views/knowledge/components/card/behaviors/common/CardLightbox';
import { SlideImage } from 'yet-another-react-lightbox';
import { EditUserHeader } from './EditUserHeader';

////////////////////////////////////////////////////////////////////////////

type Options = "remove" | "download";
interface IProps {
    userId: string;
    userName: string;
    editMode: "default" | "profile";
    viewMode?: "dialog" | "form";
    onClose: () => void;
    canAddFiles: boolean;
    canDeleteFiles: boolean;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Edição de Usuário - Arquivos de prontuário
 * @param props
 */
export const EditUserRecordFiles: React.FC<IProps> = (props) => {

    const { userId, userName, viewMode, onClose, editMode, canAddFiles, canDeleteFiles } = props;

    const api = useCoreApi();
    const { user } = useSession();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [dialog, setDialog] = useState<any>();
    const [viewData, setViewData] = useState<ManagedUserRecordFilesResponsePaginatedList | null>(null);
    const [viewError, setViewError] = useState<any>(null);
    const [deleting, setDeleting] = useState<string | null>(null);
    const [searchText, setSearchText] = useState<string>("");
    const pageNumber = useRef<number>(1);

    const filtered = searchText.length > 0;

    const getData = () => {
        setLoading(true);
        api.v1.userManagement.getRecordFiles({
            userId,
            searchText: searchText,
            pageNumber: pageNumber.current,
            pageSize: APP_PAGE_SIZE_DEFAULT
        }).then(({ data }) => setViewData(data))
            .catch(e => {
                setViewData(null);
                setViewError(e as Error);
            })
            .finally(() => setLoading(false));
    };


    ///
    const loadPage = (page: number) => {
        pageNumber.current = page;
        getData();
    };

    ///
    const handleResetFilter = () => {
        setSearchText("");
        loadPage(1);
    };

    ///
    const handleOpenFile = (data: ManagedUserRecordFilesResponse) => {
        const file = data.file;
        if (MediaUtils.isTextOrCsv(file.mimetype)) {
            setDialog(<MprcTextViewer
                title={file.metadata.filename ?? file.mimetype}
                onGetData={async () => {
                    const response = await fetch(file.url);
                    return response.text();
                }}
                onError={() => MediaUtils.openFileViewerOrDownloadUrl(file.metadata.filename, file.url, file.fileId)}
                onClose={() => setDialog(null)}
            />
            );
        }
        else if (MediaUtils.isImage(file.mimetype)) {
            const slides: SlideImage[] = [
                {
                    type: "image",
                    title: file.metadata.filename,
                    src: file.url,
                    height: file.metadata.height!,
                    width: file.metadata.width!,
                    imageFit: (file.metadata.width ?? 0) < (file.metadata.height ?? 0) ? "contain" : "cover",
                    srcSet: [{ src: file.url, width: file.metadata.width!, height: file.metadata.height! }]
                }
            ];

            setDialog(
                <CardLightBox
                    slides={slides}
                    open={true}
                    index={0}
                    autoplay={false}
                    rotationInterval={0}
                    onClose={() => setDialog(null)}
                />
            );
        }
        else {
            MediaUtils.openFileViewerOrDownloadUrl(file.metadata.filename, file.url, file.fileId);
        }
    };

    ///
    const handleDownload = (data: ManagedUserRecordFilesResponse) => {
        HtmlUtils.downloadUrl(data.file.url, data.file.metadata.filename);
    };


    ///
    const handleAddFile = (file: FileUploadResultDto) => {
        setSaving(true);

        api.v1.userManagement.addRecordFile({ userId, fileId: file.fileId })
            .then(({ data: success }) => {
                if (success) {
                    enqueueSnackbar(t("User.Edit.RecordFiles.AddSuccessMessage"), { variant: "success" });
                    setViewData(state => ({
                        ...state!, items: [
                            {
                                file: {
                                    url: file.url,
                                    fileId: file.fileId,
                                    filename: file.metadata.filename ?? "",
                                    metadata: file.metadata,
                                    mimetype: file.mimetype
                                },
                                createdOn: undefined!,
                                createdBy: user.userId,
                                creator: user.name,
                                canDelete: true
                            }, ...state!.items],
                        totalRows: state!.totalRows + 1
                    }));
                }
                else {
                    enqueueSnackbar(t("User.Edit.RecordFiles.AddFailMessage"), { variant: "error" });
                }
            })
            .catch(e => MprcRequestSnackbarErrorReport(e))
            .finally(() => setSaving(false));
    };

    ///
    const handleDelete = (fileId: string) => {
        setDialog(
            <MprcConfirmDialog
                open
                message={t("User.Edit.RecordFiles.ConfirmDeleteMessage")}
                onClose={(yes) => {
                    if (yes) setDeleting(fileId);
                    setDialog(null);
                }}
            />
        );
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////////

    // Ao remover...
    useEffect(() => {
        async function postData() {

            if (deleting) {
                try {
                    if ((await api.v1.userManagement.deleteRecordFile({ userId, fileId: deleting })).data === true) {
                        enqueueSnackbar(t("User.Edit.RecordFiles.DeleteSuccessMessage"), { variant: "success" });
                        loadPage(viewData!.items.length == 1 && pageNumber.current > 1 ? pageNumber.current-- : pageNumber.current);
                    }
                    else
                        enqueueSnackbar(t("User.Edit.RecordFiles.DeleteFailMessage"), { variant: "error" });
                }
                catch (e) {
                    setViewError(e);
                }
            }
            setDeleting(null);
        }

        postData();

    }, [deleting]);

    // Ao carregar...
    useEffect(() => loadPage(1), [searchText]);

    ////////////////////////////////////////////////////////////////////////////////

    ///
    const RenderFilter = (
        <MprcCardFilterBox>
            <MprcFilterBoxContent title={t("User.Edit.RecordFiles.Title")}>
                {canAddFiles &&
                    <MprcContainer>
                        <Box width="450px">
                            <MprcFileSelection
                                acceptedFiles={
                                    {
                                        "application/pdf": [".pdf"],
                                        "application/msword": [".doc"],
                                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
                                        "text/plain": [".txt"],
                                        //"image/*": [".png", '.jpg', '.jpeg']
                                    }
                                }
                                enableClear={false}
                                onClear={() => { }}
                                onFileUploadSuccess={result => handleAddFile(result.mainFile)}
                            />
                        </Box>
                    </MprcContainer>
                }
                <MprcSearchTextField
                    placeholder={t("User.Edit.RecordFiles.SearchTextPlaceholder")}
                    initialValue={searchText}
                    confirmMode="onEnter"
                    onConfirm={(value) => setSearchText(value)}
                />
            </MprcFilterBoxContent>
        </MprcCardFilterBox>
    );

    ///
    const getMenuItems = (item: ManagedUserRecordFilesResponse): MprcDropDownItemAny[] => {

        const group1: MprcDropdownItems<Options> = [];
        const group2: MprcDropdownItems<Options> = [];

        if (item.createdBy == user.userId || userId == user.userId) {
            group1.push({ text: t("Actions.Download", { ns: "shared" }), value: "download" });
        }

        if (canDeleteFiles || (canAddFiles && item.createdBy == user.userId)) {
            group2.push({ text: <MprcText color="error">{t("Actions.Delete", { ns: "shared" })}</MprcText>, value: "remove" });
        }

        return MprcDropdownBuildGroupedItems<Options>([group1, group2]);
    };

    ///
    const handleOption = (item: ManagedUserRecordFilesResponse, selectedOption: Options) => {
        if (selectedOption == "remove") handleDelete(item.file.fileId);
        else if (selectedOption == "download") handleDownload(item);
    };


    ///
    const RenderGrid =
        <>
            <Grid container spacing={2}>
                {viewData?.items.map((item) =>
                    <Grid container key={item.file.fileId} alignItems="start" justifyContent="space-between">
                        <Grid size="grow">
                            <Stack direction="column" alignItems="flex-start" justifyContent="center">
                                <MprcLink onClick={() => handleOpenFile(item)}>
                                    <Typography variant="h5">{LibUtils.getFilenameWithoutExtension(item.file.metadata.filename)}</Typography>
                                </MprcLink>
                                <Typography variant="caption">
                                    <MprcJoinComponents
                                        separator="&bull;"
                                        components={
                                            [
                                                LibUtils.getFileExtension(item.file.metadata.filename)?.toUpperCase(),
                                                <>{t("User.Edit.RecordFiles.AddedByText")}&nbsp;{item.creator}</>,
                                                item.createdOn ? Formaters.performaDate(item.createdOn, { preposition: "none", excludeTime: true, replaceClosestDays: true }) : t("shared:Literal.Now")
                                            ]
                                        }
                                    />
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid>
                            <MprcIconButtonMoreOptionsDropdown
                                items={getMenuItems(item)}
                                onChange={(selectedOption) => handleOption(item, selectedOption)}
                            />
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </>;

    ///
    const RenderContent = () => {
        return (
            <>
                <MprcRequestIndicator
                    visible={loading || saving || deleting != undefined}
                    type={loading ? "request" : "post"}
                />

                <MprcRequestErrorDialog
                    error={viewError}
                    onClose={() => { setViewError(undefined); }}
                />

                {dialog}

                <MprcContainer>
                    <MprcCard>
                        {RenderFilter}
                        <CardContent>
                            <MprcContainer margin="dense">
                                <MprcFilterCount
                                    loading={loading}
                                    isEmpty={(viewData?.totalRows ?? 0) == 0}
                                    countText={t("User.Edit.RecordFiles.RecordsFoundText", { count: viewData?.totalRows ?? 0 })}
                                    filter={{
                                        active: filtered,
                                        text: t("User.Edit.RecordFiles.RecordsFilteredText", { count: viewData?.totalRows ?? 0 }),
                                        onResetFilter: handleResetFilter
                                    }}
                                />
                            </MprcContainer>
                            {viewData && viewData.totalRows > 0 && RenderGrid}

                            {viewData && viewData.totalPages > 1 &&
                                <MprcCardActions>
                                    <Pagination
                                        shape="rounded"
                                        color="primary"
                                        page={viewData.pageNumber}
                                        count={viewData.totalPages}
                                        onChange={(e, page) => loadPage(page)}
                                    />
                                </MprcCardActions>
                            }

                        </CardContent>
                    </MprcCard>
                </MprcContainer>
            </>
        );
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {
                viewMode == "dialog"
                    ? (
                        <Dialog
                            open
                            fullWidth
                            maxWidth="lg"
                        >
                            <MprcDialogTitleClose
                                title={userName}
                                onClose={onClose}
                            />
                            <DialogContent sx={{ height: "100vh" }}>
                                {RenderContent()}
                            </DialogContent>
                        </Dialog>
                    )
                    : (
                        <Grid container direction="column" flexWrap="nowrap" spacing={2} height="100%">
                            <Grid>
                                <EditUserHeader
                                    onClose={onClose}
                                    recordChanged={false}
                                    reloadOnClose={false}
                                    actions={[
                                        <MprcBarTitle>
                                            {editMode == "profile" && userId == user.userId ? t("User.Common.YourProfileText") : userName}
                                        </MprcBarTitle>
                                    ]}
                                />
                            </Grid>
                            <Grid size="grow" height="100%">
                                {RenderContent()}
                            </Grid>
                        </Grid>
                    )
            }
        </>
    );
};
